/* eslint-env browser */
// Users › Add / edit a customer, driver, fleet admin or admin (wave 3,
// W2_Portal_Settings look): the photo card beside titled cards (personal
// details, bank, account), one label above each field, errors under the
// field that is wrong, one button in a bar that stays in view.
//
// Data and writes, exactly as before (memory: portal-full-record-overwrite):
// - the person from the user list already loaded (fetchUsersOnce on
//   arrival); an unknown id goes to /404.
// - Update (critical edits on): editUser(id, { ...record, ...edits }, record
//   as loaded) — editUser writes only what differs; lowering approval also
//   clears adminApprovedTrue; then fetchUsersOnce. A refusal keeps the form
//   open with the reason; no answer within 8 s leaves the page as before. A
//   new photo goes through updateCustomerProfileImage. Changing the user type
//   skips the field checks, as before.
// - Add: the same checks, the phone as +251…, checkUserExists, then
//   addUser({ ...form, createdAt, approved, walletBalance: 0, referralId,
//   usertype and the type's defaults, lang, donkeyMiles: 0 }); the form is
//   cleared for the next person two seconds later, as before.
// - Back and Save return to the person's list tab with the page number.
// Phone entry takes 9 plain digits (+251 outside the box).
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { api } from 'common';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import { tokens as c, layout } from '../theme';
import { showToast, InlineMessage } from '../components/feedback';
import { processApiResponse } from '../utils/securityUtils';
import { BackButton, PageHeader, SectionCard, FieldLabel, FieldError, InfoRow, calmCaps, toNineDigits } from '../components/modern';

const EMAIL_RE =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const bankList = [
  { id: 130, name: 'Abay Bank' },
  { id: 772, name: 'Addis International Bank' },
  { id: 207, name: 'Ahadu Bank' },
  { id: 656, name: 'Awash Bank' },
  { id: 347, name: 'Bank of Abyssinia' },
  { id: 571, name: 'Berhan Bank' },
  { id: 128, name: 'CBEBirr' },
  { id: 946, name: 'Commercial Bank of Ethiopia (CBE)' },
  { id: 893, name: 'Coopay-Ebirr' },
  { id: 880, name: 'Dashen Bank' },
  { id: 301, name: 'Global Bank Ethiopia' },
  { id: 534, name: 'Hibret Bank' },
  { id: 315, name: 'Lion International Bank' },
  { id: 266, name: 'M-Pesa' },
  { id: 979, name: 'Nib International Bank' },
  { id: 423, name: 'Oromia International Bank' },
  { id: 855, name: 'telebirr' },
  { id: 472, name: 'Wegagen Bank' },
  { id: 687, name: 'Zemen Bank' },
];

// The users tab each type returns to (customers 0, drivers 1, fleet admins
// 2, admins 4 — the All admins tab).
const TAB_OF = { customer: 0, driver: 1, fleetadmin: 2, admin: 4 };

function EditUser() {
  const { id, usertype } = useParams();
  const { t, i18n } = useTranslation();
  const languagedata = useSelector((state) => state.languagedata);
  const [langSelection, setLangSelection] = useState(1);
  const [multiLanguage, setMultiLanguage] = useState({});
  const staticusers = useSelector((state) => state.usersdata.staticusers);
  const settings = useSelector((state) => state.settingsdata.settings);
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loaded = useRef(false);
  const fileInputRef = useRef();
  const { editUser, fetchUsersOnce, updateCustomerProfileImage, checkUserExists, addUser } = api;
  const [data, setData] = useState(null);
  const [oldData, setOldData] = useState(null);
  const [profileImage, setProfileImage] = useState(null);
  const [profilePreview, setProfilePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fleetAdmins, setFleetAdmins] = useState([]);
  const [approved, setApproved] = useState(auth.profile.usertype === 'callcenteragent' || auth.profile.usertype === 'callcentersupervisor');
  const [signUpReferraldata, setsignupReferralData] = useState('');
  const [isManualBank, setIsManualBank] = useState(false);
  const [errors, setErrors] = useState({});
  const [formMessage, setFormMessage] = useState(null); // { kind, text }
  const { state } = useLocation();

  const L = (key, dflt, opts) => t(key, { defaultValue: dflt, ...(opts || {}) });
  const critical = !!settings.AllowCriticalEditsAdmin;

  useEffect(() => {
    if (languagedata.langlist) {
      setMultiLanguage(languagedata.langlist);
    }
  }, [languagedata.langlist]);

  useEffect(() => {
    dispatch(fetchUsersOnce());
  }, [dispatch, fetchUsersOnce]);

  useEffect(() => {
    if (id && usertype) {
      if (staticusers) {
        const user = staticusers.filter((u) => u.id === id.toString() && u.usertype === usertype.toString())[0];
        const refferedUser = staticusers.filter((value) => value.id === user?.signupViaReferral);
        if (refferedUser.length > 0) {
          const refferedData = `${refferedUser[0].firstName} ${refferedUser[0].lastName} (${refferedUser[0].mobile} ) ${refferedUser[0].email}`;
          setsignupReferralData(refferedData);
        }
        if (!user) {
          navigate('/404');
        }
        setData(user);
        setOldData(user);
      }
    } else if (auth.profile.usertype === 'callcenteragent' || auth.profile.usertype === 'callcentersupervisor') {
      const currentUser = staticusers ? staticusers.find((user) => user.id === auth.profile.uid) : null;
      if (currentUser) {
        if (!data?.fleetadmin) {
          setData((prevData) => ({
            ...prevData,
            fleetadmin: currentUser.id,
          }));
        }
      } else {
        setData([]);
      }
    } else {
      setData([]);
    }
    loaded.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [staticusers, id, navigate, usertype]);

  // Quick search's "Add … as a new customer" hands the number over as 9
  // plain digits: the new customer's form starts with it. Runs after the
  // effect above, which can reset the form when the users list arrives.
  const handedMobile = !id && usertype === 'customer' && /^\d{9}$/.test(String(state?.mobile || '')) ? String(state.mobile) : '';
  useEffect(() => {
    if (!handedMobile) return;
    setData((prev) => {
      const base = prev && !Array.isArray(prev) ? prev : {};
      return base.mobile ? prev : { ...base, mobile: handedMobile };
    });
  }, [handedMobile, staticusers]);

  // The agents (or fleet admins) a driver can be linked to, as before.
  useEffect(() => {
    if (staticusers && auth && auth.profile) {
      const listOf = (arr) =>
        arr.map((user) => ({
          id: user.id,
          desc:
            user.firstName +
            ' ' +
            user.lastName +
            ' (' +
            (settings.AllowCriticalEditsAdmin ? user.mobile : t('hidden_demo')) +
            ') ' +
            (settings.AllowCriticalEditsAdmin ? user.email : t('hidden_demo')),
        }));
      if (auth.profile.usertype === 'admin') {
        setFleetAdmins(listOf(staticusers.filter((user) => user.usertype === 'fleetadmin')));
      } else if (auth.profile.usertype === 'callcenteragent' || auth.profile.usertype === 'callcentersupervisor') {
        setFleetAdmins(listOf(staticusers.filter((user) => user.usertype === 'callcenteragent' || user.usertype === 'callcentersupervisor')));
      }
    }
  }, [staticusers, auth.profile.usertype, auth.profile.uid, settings.AllowCriticalEditsAdmin, auth, t]);

  // The chosen photo's preview (released when it changes).
  useEffect(() => {
    if (!profileImage) {
      setProfilePreview(null);
      return undefined;
    }
    const url = window.URL.createObjectURL(profileImage);
    setProfilePreview(url);
    return () => window.URL.revokeObjectURL(url);
  }, [profileImage]);

  const clearProblem = (name) => {
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
    if (formMessage) setFormMessage(null);
  };

  const profileImageChange = async (e) => {
    setProfileImage(e.target.files[0]);
  };

  const handleInputChange = (e) => {
    const { id: field, value } = e.target;
    const updatedData = { ...data, [field]: value };

    // Auto-generate email when the first name or last name is entered and email is empty
    if (field === 'firstName' || field === 'lastName') {
      const firstName = (field === 'firstName' ? value : data.firstName || '').toLowerCase().replace(/[^a-z0-9]/g, '');
      const lastName = (field === 'lastName' ? value : data.lastName || '').toLowerCase().replace(/[^a-z0-9]/g, '');

      // Only auto-update email if it's empty or matches the auto-generated pattern
      const currentEmail = data.email || '';

      // Check if current email is empty or is an auto-generated one (ends with @donkeydrive.io)
      if (!currentEmail || currentEmail.endsWith('@donkeydrive.io')) {
        // Only generate if both names are present
        if (firstName && lastName) {
          updatedData.email = `${firstName}.${lastName}@donkeydrive.io`;
        } else if (firstName && !lastName) {
          // If only first name, still generate but without last name
          updatedData.email = `${firstName}@donkeydrive.io`;
        } else if (!firstName && lastName) {
          // If only last name, still generate but without first name
          updatedData.email = `${lastName}@donkeydrive.io`;
        }
      }
    }
    setData(updatedData);
    clearProblem(field === 'firstName' || field === 'lastName' ? 'name' : field);
  };

  const handleChangeApproved = (event) => {
    setApproved(event.target.value);
    setData({ ...data, approved: event.target.value });
  };

  const handleChangeFleetAdmin = (event) => {
    setData({
      ...data,
      fleetadmin: event.target.value,
    });
  };

  const backToList = () => {
    if (Object.prototype.hasOwnProperty.call(TAB_OF, usertype)) {
      navigate(`/users/${TAB_OF[usertype]}`, { state: { pageNo: state?.pageNo } });
    }
  };

  const navigateToInfo = () => {
    setTimeout(() => {
      setProfileImage(null);
      backToList();
      setLoading(false);
    }, 1000);
  };

  const handleUpdate = () => {
    // Create a copy of data to modify
    let updatedData = { ...data };

    // Auto-generate email if blank using first and last name
    if ((!updatedData.email || updatedData.email.trim() === '') && updatedData.firstName && updatedData.lastName) {
      // Generate email from first and last name
      const firstName = updatedData.firstName.toLowerCase().replace(/[^a-z0-9]/g, '');
      const lastName = updatedData.lastName.toLowerCase().replace(/[^a-z0-9]/g, '');

      if (firstName && lastName) {
        // Generate email in format: firstname.lastname@donkeydrive.io
        updatedData.email = `${firstName}.${lastName}@donkeydrive.io`;

        // Update the state immediately to show in the field
        setData(updatedData);

        // Small delay to ensure UI updates before validation
        setTimeout(() => {
          // Continue with the update process
          handleUpdateWithData(updatedData);
        }, 100);
        return; // Exit early to allow state update
      }
    }

    // If no email generation needed, proceed normally
    handleUpdateWithData(updatedData);
  };

  const handleUpdateWithData = (updatedData) => {
    setFormMessage(null);
    setErrors({});
    // Check if usertype has been changed - if so, bypass all validations
    const isUsertypeChanged = oldData?.usertype && updatedData?.usertype && oldData.usertype !== updatedData.usertype;

    // Only apply validations if usertype hasn't changed
    if (!isUsertypeChanged) {
      if (!(updatedData.lastName && updatedData.firstName)) {
        setErrors({ name: t('proper_input_name') });
        return;
      } else if (!EMAIL_RE.test(updatedData.email)) {
        setErrors({ email: t('proper_email') });
        return;
      } else if (!updatedData.mobile) {
        setErrors({ mobile: t('proper_mobile') });
        return;
      } else if (usertype === 'driver' && !updatedData.bankName && auth.profile.usertype !== 'callcentersupervisor') {
        setErrors({ bank: t('proper_bank_name') });
        return;
      } else if (usertype === 'driver' && !updatedData.bankAccount && auth.profile.usertype !== 'callcentersupervisor') {
        setErrors({ bankAccount: t('proper_bank_account_number') });
        return;
      }
    }

    // Proceed with update (either validations passed or usertype was changed)
    if (settings.AllowCriticalEditsAdmin) {
      setLoading(true);

      if (profileImage) {
        updateCustomerProfileImage(profileImage, updatedData.id).then(() => {
          dispatch(fetchUsersOnce());
          navigateToInfo();
        });
      }
      if (JSON.stringify(oldData) !== JSON.stringify(updatedData) && Object.keys(updatedData).length !== 0) {
        const toSave = { ...updatedData };
        // Lowering approval also clears a stale adminApprovedTrue (same
        // rule as the Drivers switch and the clearance letter): the
        // role switch trusts that flag, so an un-approved account could
        // switch role and come back approved.
        if (oldData && oldData.approved === true && toSave.approved === false && oldData.adminApprovedTrue === true) {
          toSave.adminApprovedTrue = false;
        }
        // oldData = the row as it was loaded: editUser writes only the
        // fields this form changed and never the cached row's
        // server-owned money / bonus fields (a stale echo would roll
        // back newer balances, and the database rules refuse it, which
        // fails the whole save).
        const saved = dispatch(editUser(updatedData.id, toSave, oldData));
        dispatch(fetchUsersOnce());
        if (saved && typeof saved.then === 'function') {
          // editUser hands back the write's promise: leave the page
          // once the database accepted it; a refusal (rules) comes
          // back within one round trip and keeps the form open. A
          // write still unanswered after 8 s (no connection: it stays
          // queued) leaves the page as before.
          let settled = false;
          const leaveAnyway = setTimeout(() => {
            if (!settled) {
              settled = true;
              navigateToInfo();
            }
          }, 8000);
          saved
            .then((r) => {
              if (r && r.success === false) throw new Error('refused');
              clearTimeout(leaveAnyway);
              if (!settled) {
                settled = true;
                showToast.success(L('saved', 'Saved'));
                navigateToInfo();
              }
            })
            .catch(() => {
              clearTimeout(leaveAnyway);
              if (settled) return; // the page was already left
              settled = true;
              setLoading(false);
              setFormMessage({
                kind: 'error',
                text: t('edit_user_save_failed', {
                  defaultValue: 'The changes were not saved. Reload the page and try again. If it still fails, your role cannot make this change.',
                }),
              });
            });
        } else {
          navigateToInfo();
        }
      }
      if (JSON.stringify(oldData) === JSON.stringify(updatedData) && !profileImage) {
        setFormMessage({ kind: 'info', text: t('make_changes_to_update') });
        setLoading(false);
      }
    } else {
      setFormMessage({ kind: 'error', text: t('demo_mode') });
    }
    loaded.current = true;
  };

  const normalizePhone = (input) => {
    if (!input) {
      return null;
    }
    const cleaned = input.trim().replace(/\D/g, '');
    // +251XXXXXXXXX
    if (/^251[1-9]\d{8}$/.test(cleaned)) return `+${cleaned}`;
    // national without leading zero
    if (/^[1-9]\d{8}$/.test(cleaned)) return `+251${cleaned}`;
    // local with leading zero
    if (/^0[1-9]\d{8}$/.test(cleaned)) return `+251${cleaned.slice(1)}`;
    return null;
  };

  const handleAdd = () => {
    // Create a copy of data to modify
    let updatedData = { ...data };

    // Auto-generate email if blank using first and last name
    if ((!updatedData.email || updatedData.email.trim() === '') && updatedData.firstName && updatedData.lastName) {
      // Generate email from first and last name
      const firstName = updatedData.firstName.toLowerCase().replace(/[^a-z0-9]/g, '');
      const lastName = updatedData.lastName.toLowerCase().replace(/[^a-z0-9]/g, '');

      if (firstName && lastName) {
        // Generate email in format: firstname.lastname@donkeydrive.io
        updatedData.email = `${firstName}.${lastName}@donkeydrive.io`;

        // Update the state immediately to show in the field
        setData(updatedData);

        // Small delay to ensure UI updates before validation
        setTimeout(() => {
          // Continue with the add process
          handleAddWithData(updatedData);
        }, 100);
        return; // Exit early to allow state update
      }
    }

    // If no email generation needed, proceed normally
    handleAddWithData(updatedData);
  };

  const handleAddWithData = (updatedData) => {
    setFormMessage(null);
    setErrors({});
    if (!(updatedData.lastName && updatedData.firstName)) {
      setErrors({ name: t('proper_input_name') });
    } else if (!EMAIL_RE.test(updatedData.email)) {
      setErrors({ email: t('proper_email') });
    } else if (!updatedData.mobile || !normalizePhone(updatedData.mobile)) {
      setErrors({ mobile: t('proper_mobile') });
    } else if (!updatedData.verifyId && (auth.profile.usertype !== 'callcenteragent' || auth.profile.usertype !== 'callcentersupervisor')) {
      setErrors({ verifyId: t('proper_verify_id', { defaultValue: 'Please enter the ID or passport number properly.' }) });
    } else {
      new Promise((resolve, reject) => {
        setLoading(true);
        setTimeout(() => {
          updatedData.mobile = normalizePhone(updatedData.mobile);
          checkUserExists(updatedData)
            .then(async (res) => {
              const decryptedRes = await processApiResponse(res);
              if (decryptedRes.users && decryptedRes.users.length > 0) {
                setFormMessage({ kind: 'error', text: t('user_exists') });
                reject(new Error('User already exists'));
              } else if (decryptedRes.error) {
                setFormMessage({ kind: 'error', text: t('email_or_mobile_issue') });
                reject(new Error('Email or Mobile issue'));
              } else {
                const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
                const reference = [...Array(5)].map(() => chars[~~(Math.random() * chars.length)]).join('');
                updatedData['createdAt'] = new Date().getTime();
                let role = auth.profile.usertype;
                updatedData['approved'] = approved;
                updatedData['walletBalance'] = 0;
                if (usertype === 'customer') {
                  updatedData['usertype'] = 'customer';
                  updatedData['referralId'] = reference;
                  if (role === 'fleetadmin') {
                    updatedData['fleetadmin'] = auth.profile.uid;
                  }
                  resolve();
                } else if (usertype === 'driver') {
                  updatedData['driverActiveStatus'] = false;
                  updatedData['usertype'] = 'driver';
                  updatedData['referralId'] = reference;
                  updatedData['queue'] = false;
                  if (role === 'fleetadmin') {
                    updatedData['fleetadmin'] = auth.profile.uid;
                  }
                  if (approved === true) {
                    updatedData['adminApprovedTrue'] = true;
                  } else {
                    updatedData['adminApprovedTrue'] = false;
                  }
                  resolve();
                } else if (usertype === 'fleetadmin') {
                  updatedData['usertype'] = 'fleetadmin';
                  updatedData['createdAt'] = new Date().getTime();
                  resolve();
                } else if (usertype === 'admin') {
                  updatedData['usertype'] = usertype.toString();
                  updatedData['createdAt'] = new Date().getTime();
                  updatedData['approved'] = true;
                  resolve();
                } else {
                  setFormMessage({ kind: 'error', text: t('invalid_userType_add', { defaultValue: 'This user type cannot be added here.' }) });
                  // Nothing is added for a user type this form does not
                  // know (the old code went on and added it untyped).
                  reject(new Error('Invalid usertype Add'));
                  return;
                }
                let arr = Object.keys(languagedata.langlist);
                updatedData['lang'] = { langLocale: multiLanguage[arr[langSelection]].langLocale, dateLocale: multiLanguage[arr[langSelection]].dateLocale };
                updatedData['donkeyMiles'] = 0;
                dispatch(addUser(updatedData));
                // Show success message
                showToast.success(usertype === 'driver' ? t('driver_added_successfully') : t('rider_added_successfully'));
                // Clear form and prepare for new entry after 2 seconds
                setTimeout(() => {
                  // Reset form for new entry
                  setData({
                    firstName: '',
                    lastName: '',
                    mobile: '',
                    email: '',
                    usertype: usertype,
                    approved: true,
                    walletBalance: 0,
                    signupViaReferral: usertype === 'driver' ? settings?.driver_referral_bonus : settings?.rider_referral_bonus,
                  });
                  setProfileImage(null);
                  // Dispatch fetch to update the list in background
                  dispatch(fetchUsersOnce());
                }, 2000);
              }
            })
            .catch((err) => {
              setFormMessage({ kind: 'error', text: (err && err.message) || t('email_or_mobile_issue') });
              reject(err);
            });
        }, 600);
      })
        .catch((e) => {
          console.error('caught an error:', e);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  // ── Labels and visibility, as before ─────────────────────────────────
  const title = id
    ? (usertype === 'customer' && t('update_customer_title')) ||
      (usertype === 'driver' && t('update_driver_title')) ||
      (usertype === 'fleetadmin' && t('update_fleetAdmin_title')) ||
      (usertype === 'admin' && t('update_admin_title'))
    : (usertype === 'customer' && t('add_customer_title')) ||
      (usertype === 'driver' && t('add_driver_title')) ||
      (usertype === 'fleetadmin' && t('add_fleetadmin_title')) ||
      (usertype === 'admin' && t('add_admin_title'));
  const showNames = ['driver', 'customer', 'callcenteragent', 'callcentersupervisor', 'admin'].includes(usertype);
  const showTin = ['driver', 'callcenteragent', 'callcentersupervisor', 'admin'].includes(usertype);
  const showUsertype = !!id && ['admin', 'callcentersupervisor'].includes(auth.profile.usertype) && ['driver', 'customer', 'callcenteragent'].includes(usertype);
  const showAccountBlock = usertype !== 'admin';
  const showVerifyId = showAccountBlock && usertype !== 'fleetadmin';
  const showAgent = showAccountBlock && auth?.profile?.usertype !== 'fleetadmin' && usertype === 'driver';
  const showCommission =
    showAccountBlock &&
    !!id &&
    usertype === 'driver' &&
    settings?.driverFlatRateEnabled === true &&
    (!data?.fleetadmin || settings?.flatRateAllowFleetDrivers === true) &&
    auth?.profile?.usertype === 'admin';
  const agentLabel =
    auth.profile.usertype === 'callcenteragent' || auth.profile.usertype === 'callcentersupervisor' ? t('callcenteragent', { defaultValue: 'Call Center Agent' }) : t('fleetadmins');
  const agentDisabled = id ? (auth.profile.usertype === 'callcentersupervisor' ? !!oldData?.fleetadmin : true) : false;
  const namesLocked = id ? !!(oldData?.firstName && oldData?.lastName) : false;
  const approvedValue = id ? data?.approved === true : approved;
  const bankMatch = bankList.find((b) => String(b.id) === String(data?.bankCode));
  const bankSelectValue = isManualBank ? 'other' : bankMatch ? bankMatch.id : data?.bankCode ? 'other' : '';
  const agentValue = fleetAdmins.some((f) => f.id === data?.fleetadmin) ? data.fleetadmin : '';
  const langKeys = Object.keys(multiLanguage || {});
  const formReady = !!data && (!id || !!oldData);

  const describedBy = (name) => (errors[name] ? `edituser-${name}-error` : undefined);
  const textField = ({ fieldId, label, value, disabled, error, onChange, inputProps, InputProps, type, autoComplete }) => (
    <Box sx={{ minWidth: 0 }}>
      <FieldLabel htmlFor={fieldId} disabled={disabled}>
        {label}
      </FieldLabel>
      <TextField
        id={fieldId}
        fullWidth
        type={type}
        value={value}
        disabled={disabled}
        error={!!errors[error]}
        onChange={onChange || handleInputChange}
        inputProps={{ autoComplete: autoComplete || 'off', 'aria-describedby': describedBy(error), ...(inputProps || {}) }}
        InputProps={InputProps}
      />
      <FieldError id={`edituser-${error}-error`}>{errors[error]}</FieldError>
    </Box>
  );
  const grid = { display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, columnGap: 2, rowGap: 2.25, mt: 2 };

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <BackButton onClick={backToList} />
      <PageHeader title={title ? calmCaps(title) : undefined} />

      {!formReady ? (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '260px minmax(0, 1fr)' } }}>
          <Skeleton variant="rounded" height={300} />
          <Skeleton variant="rounded" height={420} />
        </Box>
      ) : (
        <Box
          component="form"
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            if (loading) return;
            if (id) handleUpdate();
            else handleAdd();
          }}
          sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: id ? '260px minmax(0, 1fr)' : 'minmax(0, 1fr)' }, alignItems: 'start' }}
        >
          {id ? (
            <Paper component="section" aria-label={t('profile_image')} sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'center' }}>
              <ButtonBase
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                aria-label={t('upload_profile_image')}
                sx={{
                  width: '100%',
                  maxWidth: 220,
                  aspectRatio: '4 / 5',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  border: `2px dashed ${profilePreview ? c.ACCENT : c.OUTLINE_STRONG}`,
                  bgcolor: c.SURFACE_ALT,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  color: c.INK_2,
                  '&:hover': { borderColor: c.ACCENT },
                  '&:focus-visible': { outline: `2px solid ${c.ACCENT}`, outlineOffset: 2 },
                }}
              >
                {profilePreview || data?.profile_image ? (
                  <Box component="img" src={profilePreview || data.profile_image} alt={t('profile_image')} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <>
                    <FileUploadOutlinedIcon sx={{ fontSize: 44, color: c.ACCENT }} />
                    <Typography sx={{ fontSize: 14, fontWeight: 600, textAlign: 'center', px: 1 }}>{t('upload_profile_image')}</Typography>
                  </>
                )}
              </ButtonBase>
              <input onChange={(event) => profileImageChange(event)} multiple={false} ref={fileInputRef} type="file" hidden />
              {profilePreview || data?.profile_image ? (
                <Button variant="text" startIcon={<FileUploadOutlinedIcon />} onClick={() => fileInputRef.current && fileInputRef.current.click()}>
                  {t('upload_profile_image')}
                </Button>
              ) : null}
            </Paper>
          ) : null}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            <SectionCard icon={<PersonOutlineRoundedIcon />} hue="info" title={L('personal_info', 'Personal Info')} id="edituser-personal">
              <Box sx={grid}>
                {showNames ? (
                  <>
                    {textField({ fieldId: 'firstName', label: t('firstname'), value: data?.firstName || '', disabled: namesLocked, error: 'name', autoComplete: 'given-name' })}
                    {textField({ fieldId: 'lastName', label: t('last_name'), value: data?.lastName || '', disabled: namesLocked, error: 'nameLast', autoComplete: 'family-name' })}
                  </>
                ) : null}
                {textField({
                  fieldId: 'mobile',
                  label: t('mobile'),
                  type: id ? 'text' : 'tel',
                  value: id ? (critical ? data?.mobile || '' : t('hidden_demo')) : data?.mobile || '',
                  disabled: !!id,
                  error: 'mobile',
                  onChange: (e) => {
                    // 9 plain digits: spaces, dashes, a leading 0 and a pasted +251 are dropped.
                    const justDigits = toNineDigits(e.target.value);
                    setData({ ...data, mobile: justDigits });
                    clearProblem('mobile');
                  },
                  inputProps: id ? { dir: 'ltr' } : { inputMode: 'numeric', maxLength: 9, dir: 'ltr' },
                  InputProps: id
                    ? undefined
                    : {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Box component="span" sx={{ color: c.INK_2, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                              +{settings.countryCode}
                            </Box>
                          </InputAdornment>
                        ),
                      },
                })}
                {textField({
                  fieldId: 'email',
                  label: t('email'),
                  type: 'email',
                  value: id ? (critical ? data?.email || '' : t('hidden_demo')) : data?.email || '',
                  disabled: !!id,
                  error: 'email',
                  inputProps: { inputMode: 'email', spellCheck: false },
                })}
                {showVerifyId
                  ? textField({ fieldId: 'verifyId', label: t('verify_id'), value: data?.verifyId || '', disabled: !!id, error: 'verifyId' })
                  : null}
                <Box sx={{ minWidth: 0 }}>
                  <FieldLabel id="language-select-label">{t('lang')}</FieldLabel>
                  <TextField
                    select
                    fullWidth
                    value={langKeys.length ? langSelection : ''}
                    onChange={(event) => setLangSelection(event.target.value)}
                    SelectProps={{ labelId: 'language-select-label', id: 'language-select' }}
                  >
                    {langKeys.map((key, index) => (
                      <MenuItem key={key} value={index}>
                        {multiLanguage[key].langName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              </Box>
              {errors.name && !showNames ? <FieldError>{errors.name}</FieldError> : null}
              {errors.verifyId && !showVerifyId ? <FieldError>{errors.verifyId}</FieldError> : null}
            </SectionCard>

            <SectionCard icon={<AccountBalanceOutlinedIcon />} hue="success" title={calmCaps(L('bankDetails', 'Bank details'))} id="edituser-bank">
              <Box sx={grid}>
                <Box sx={{ minWidth: 0 }}>
                  <FieldLabel id="bank-select-label" disabled={!!id}>
                    {t('select_bank')}
                  </FieldLabel>
                  <TextField
                    select
                    fullWidth
                    value={bankSelectValue}
                    disabled={!!id}
                    error={!!errors.bank}
                    onChange={(e) => {
                      if (e.target.value === 'other') {
                        setIsManualBank(true);
                        setData({ ...data, bankCode: '', bankName: '' });
                      } else {
                        const selectedBank = bankList.find((b) => b.id === e.target.value);
                        setIsManualBank(false);
                        setData({ ...data, bankCode: selectedBank.id, bankName: selectedBank.name });
                      }
                      clearProblem('bank');
                    }}
                    SelectProps={{ labelId: 'bank-select-label', id: 'bankSelect', SelectDisplayProps: { 'aria-describedby': describedBy('bank') } }}
                  >
                    {bankList.map((bank) => (
                      <MenuItem key={bank.id} value={bank.id}>
                        {bank.name}
                      </MenuItem>
                    ))}
                    <MenuItem value="other">{t('other')}</MenuItem>
                  </TextField>
                  <FieldError id="edituser-bank-error">{errors.bank}</FieldError>
                </Box>
                {textField({ fieldId: 'bankName', label: t('bankName'), value: data?.bankName || '', disabled: id ? true : !isManualBank, error: 'bankName' })}
                {textField({ fieldId: 'bankCode', label: t('bankCode'), value: data?.bankCode || '', disabled: id ? true : !isManualBank, error: 'bankCode' })}
                {textField({
                  fieldId: 'bankAccount',
                  label: t('bankAccount'),
                  value: data?.bankAccount || '',
                  disabled: !!id,
                  error: 'bankAccount',
                  inputProps: { inputMode: 'numeric', dir: 'ltr' },
                })}
                {showTin ? textField({ fieldId: 'tin', label: t('tin'), value: data?.tin || '', error: 'tin', inputProps: { dir: 'ltr' } }) : null}
              </Box>
            </SectionCard>

            {showUsertype || showAccountBlock ? (
              <SectionCard icon={<VerifiedUserOutlinedIcon />} hue="accent" title={L('edituser_account', 'Account')} id="edituser-account">
                <Box sx={grid}>
                  {showUsertype ? (
                    <Box sx={{ minWidth: 0 }}>
                      <FieldLabel id="usertype-select-label">{t('user_type')}</FieldLabel>
                      <TextField
                        select
                        fullWidth
                        value={data?.usertype || usertype}
                        onChange={(e) => setData({ ...data, usertype: e.target.value })}
                        SelectProps={{ labelId: 'usertype-select-label', id: 'usertype' }}
                      >
                        <MenuItem value="customer">{t('customer')}</MenuItem>
                        <MenuItem value="driver">{t('driver')}</MenuItem>
                        <MenuItem value="callcenteragent">{t('callcenteragent')}</MenuItem>
                        <MenuItem value="scout">{t('scout')}</MenuItem>
                      </TextField>
                    </Box>
                  ) : null}
                  {showAccountBlock ? (
                    <Box sx={{ minWidth: 0 }}>
                      <FieldLabel id="approve-select-label">{t('approve_status')}</FieldLabel>
                      <TextField select fullWidth value={approvedValue} onChange={handleChangeApproved} SelectProps={{ labelId: 'approve-select-label' }}>
                        <MenuItem value={true}>{t('approved')}</MenuItem>
                        <MenuItem value={false}>{t('not_approved')}</MenuItem>
                      </TextField>
                    </Box>
                  ) : null}
                  {showAgent ? (
                    <Box sx={{ minWidth: 0, gridColumn: { sm: '1 / -1' } }}>
                      <FieldLabel id="fleetadmins-label" disabled={agentDisabled}>
                        {agentLabel}
                      </FieldLabel>
                      <TextField select fullWidth value={agentValue} disabled={agentDisabled} onChange={handleChangeFleetAdmin} SelectProps={{ labelId: 'fleetadmins-label' }}>
                        {fleetAdmins.map((e) => (
                          <MenuItem key={e.id} value={e.id}>
                            {e.desc}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                  ) : null}
                  {showCommission ? (
                    <Box sx={{ minWidth: 0 }}>
                      <FieldLabel id="commission-label">{t('commission_mode', { defaultValue: 'Commission mode' })}</FieldLabel>
                      <TextField
                        select
                        fullWidth
                        value={data?.commissionMode === 'flat_daily' ? 'flat_daily' : 'per_trip'}
                        onChange={(e) => setData({ ...data, commissionMode: e.target.value, commissionModeUpdatedAt: Date.now() })}
                        SelectProps={{ labelId: 'commission-label', id: 'commissionMode' }}
                      >
                        <MenuItem value="per_trip">{t('commission_per_trip', { defaultValue: 'Per-trip commission' })}</MenuItem>
                        <MenuItem value="flat_daily">{t('commission_flat_daily', { defaultValue: 'Daily flat rate' })}</MenuItem>
                      </TextField>
                    </Box>
                  ) : null}
                </Box>
                {showAccountBlock && id ? (
                  <Box component="dl" sx={{ m: 0, mt: 2 }}>
                    <InfoRow label={t('referralId')}>{data?.referralId ? data.referralId : t('no_data_available')}</InfoRow>
                    <InfoRow label={t('signup_via_referral')}>{critical ? signUpReferraldata || t('no_data_available') : t('hidden_demo')}</InfoRow>
                  </Box>
                ) : null}
              </SectionCard>
            ) : null}

            {/* One button, kept in view at the bottom of the screen */}
            <Paper
              sx={{
                position: 'sticky',
                bottom: 0,
                zIndex: 2,
                px: { xs: 2, md: 2.5 },
                pt: 1.5,
                pb: 'calc(12px + env(safe-area-inset-bottom))',
                borderRadius: `${layout.RADIUS_CARD}px`,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
              }}
            >
              {formMessage ? <InlineMessage kind={formMessage.kind}>{formMessage.text}</InlineMessage> : null}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="submit"
                  variant="contained"
                  color={id ? 'primary' : 'success'}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={16} color="inherit" /> : id ? <CheckRoundedIcon /> : <PersonAddAltRoundedIcon />}
                  sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 160 }}
                >
                  {loading ? L('form_saving', 'Saving…') : id ? t('update', { defaultValue: 'Update' }) : calmCaps(t('add'))}
                </Button>
              </Box>
            </Paper>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default EditUser;
